import React, { useEffect, useState } from 'react'
import './ProvablyFair.scss'
import Button from 'components/ui-kit/Button/index'
import { IconCross } from 'assets/icons/index'
import Popup from 'components/ui-kit/Popup/index'
import { useDispatch, useSelector } from 'react-redux'
import { verifyProvableFairCrash } from 'redux-store/thunk/crashGame.thunk'
import { openCopySuccessToaster, openErrorToaster } from 'utils/common-services/toaster.service'
import { setProvableFairData } from 'redux-store/redux/slices/crashGame.slice'
import { copyToClipboardAsync, truncateMiddle } from 'helpers/common.helper'
import { useTheme } from 'ThemeContext'
import lollyCopyIcon from 'assets/icons/lolly-copy-button.svg'
import smokeAceCopyIcon from 'assets/icons/smokeace-copy-button.svg'

const ProvablyFair = ({
  setIsOpenProvablyFair,
  isOpenProvablyFair,
  playClickSound,
  round
}) => {
  const dispatch = useDispatch()
  const { provableFairData, loading } = useSelector(state => state?.crashGame)
  const { theme } = useTheme()
  const [hash, setHash] = useState('')
  const [activeTab, setActiveTab] = useState('seeds')
  const verificationResult = provableFairData
  const lastRoundServerSeed = round?.roundServerSeed ?? round?.serverSeed ?? round?.roundHash ?? ''
  const lastRoundSignature = round?.roundSignature ?? round?.signature ?? ''
  const copyIcon = theme === 'theme-two' ? lollyCopyIcon : smokeAceCopyIcon

  useEffect(() => {
    if (!isOpenProvablyFair) return

    setActiveTab('seeds')
    setHash('')
    dispatch(setProvableFairData(null))
  }, [isOpenProvablyFair, dispatch])

  const handleVerifyGame = () => {
    playClickSound()
    const trimmedRoundServerSeed = hash.trim()

    if (!trimmedRoundServerSeed) {
      openErrorToaster({ message: 'Server Seed is Required' })
      return
    }

    setHash(trimmedRoundServerSeed)
    dispatch(verifyProvableFairCrash({
      payload: {
        roundServerSeed: trimmedRoundServerSeed
      }
    }))
  }

  const handleClose = () => {
    setIsOpenProvablyFair(false)
    setHash('')
    dispatch(setProvableFairData(null))
  }

  const handleCopy = async (value) => {
    if (!value) return
    const copied = await copyToClipboardAsync(value)
    if (copied) {
      openCopySuccessToaster()
    } else {
      openErrorToaster({ message: 'Copy not supported.' })
    }
    playClickSound()
  }

  return (
    <Popup className='provably-fair-modal' isOpenModal={isOpenProvablyFair} handleClose={handleClose}>
      <div className='provably-fair'>
        <div className='provably-fair__header'>
          <h2>Fairness</h2>
          <Button className='provably-fair__close' aria-label='Close Provably Fair' onClick={handleClose}>
            <IconCross fill='var(--secondaryText)' className='close-icon' />
          </Button>
        </div>

        <div className='provably-fair__tabs' role='tablist' aria-label='Provably Fair options'>
          <Button
            className={`provably-fair__tab ${activeTab === 'seeds' ? 'is-active' : ''}`}
            role='tab'
            aria-selected={activeTab === 'seeds'}
            onClick={() => setActiveTab('seeds')}
          >
            Seeds
          </Button>
          <Button
            className={`provably-fair__tab ${activeTab === 'verify' ? 'is-active' : ''}`}
            role='tab'
            aria-selected={activeTab === 'verify'}
            onClick={() => setActiveTab('verify')}
          >
            Verify
          </Button>
        </div>

        {activeTab === 'seeds' ? (
          <div className='provably-fair__panel' role='tabpanel'>
            <label htmlFor='lastRoundServerSeed'>Last Round Server Seed</label>
            <div className='provably-fair__input-group'>
              <input id='lastRoundServerSeed' value={truncateMiddle(lastRoundServerSeed)} readOnly />
              <Button
                className='provably-fair__input-action'
                aria-label='Copy last round server seed'
                disabled={!lastRoundServerSeed}
                onClick={() => handleCopy(lastRoundServerSeed)}
              >
                <img src={copyIcon} alt='' />
              </Button>
            </div>
            <label htmlFor='lastRoundSignature'>Round Signature</label>
            <div className='provably-fair__input-group'>
              <input id='lastRoundSignature' value={truncateMiddle(lastRoundSignature)} readOnly />
              <Button
                className='provably-fair__input-action'
                aria-label='Copy round signature'
                disabled={!lastRoundSignature}
                onClick={() => handleCopy(lastRoundSignature)}
              >
                <img src={copyIcon} alt='' />
              </Button>
            </div>
          </div>
        ) : (
          <div className='provably-fair__panel' role='tabpanel'>
            {verificationResult
              ? (
                <div className='provably-fair__result'>
                  <span>Multiplier</span>
                  <strong>{Number(verificationResult?.crashRate).toFixed(2)}x</strong>
                </div>
                )
              : (
                <p className='provably-fair__verification-message'>Correct inputs are required to verify result</p>
                )}
            <label htmlFor='serverSeed'>Server Seed</label>
            <input
              id='serverSeed'
              value={hash}
              placeholder='Enter Server Seed'
              autoComplete='off'
              onChange={(e) => {
                setHash(e.target.value)
                if (verificationResult) {
                  dispatch(setProvableFairData(null))
                }
              }}
            />
            {loading
              ? <Button className='provably-fair__verify' disabled>Loading</Button>
              : <Button className='provably-fair__verify' onClick={handleVerifyGame}>Verify Game</Button>}
          </div>
        )}
      </div>
    </Popup>
  )
}
export default ProvablyFair
